import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import monetaryMovementService from '../services/monetaryMovementService';
import monetaryAccountService from '../services/monetaryAccountService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { todayInTimezone } from '../utils/datetime';
import '../styles/general.css';

const emptyForm = (account, today) => ({
  account: account ? String(account.id) : '',
  movement_type: 'inflow',
  effective_date: today,
  description: '',
  amount: ''
});

/**
 * Record one movement against an account.
 *
 * A modal rather than a route: opened from a row it would otherwise throw
 * away the list's filters and page, and opened from the detail page it would
 * navigate away from the very table the new movement should appear in.
 *
 * The account arrives preselected but stays editable — a wrong click should
 * be fixable without closing anything.
 *
 * The checks here duplicate the serializer's to give a fast answer. The
 * backend re-runs all of them and remains the guarantee.
 */
const RegisterMovementModal = ({ account, isOpen, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { timezone } = useContext(AuthContext);

  // todayInTimezone, not new Date().toISOString(): the latter is UTC, so a
  // user in Monterrey opening this after 18:00 would get tomorrow's date
  // prefilled — the very error the DateField was chosen to avoid.
  const today = todayInTimezone(timezone);

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(emptyForm(account, today));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      // Active only: a movement cannot be recorded against a deactivated
      // account, so offering one would only produce a 400.
      const response = await monetaryAccountService.listAccounts({ is_active: 'true' });
      setAccounts(response.data.results || []);
    } catch (err) {
      console.error('Error loading accounts for the movement form:', err);
      notify.error(t('accounting.failedToLoad'));
    }
  }, [notify, t]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm(account, today));
    setErrors({});
    loadAccounts();
  }, [isOpen, account, today, loadAccounts]);

  if (!isOpen) return null;

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    const found = {};

    if (!form.account) found.account = t('accounting.accountRequired');
    if (!form.description.trim()) found.description = t('accounting.descriptionRequired');

    const amount = parseFloat(form.amount);

    if (form.amount === '' || Number.isNaN(amount)) {
      found.amount = t('accounting.amountRequired');
    } else if (amount === 0) {
      found.amount = t('accounting.amountCannotBeZero');
    } else if (amount < 0 && form.movement_type !== 'adjustment') {
      found.amount = t('accounting.onlyAdjustmentCanBeNegative');
    }

    return found;
  };

  const applyServerErrors = (err) => {
    const data = err?.response?.data;

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const mapped = {};
      Object.entries(data).forEach(([field, messages]) => {
        mapped[field] = Array.isArray(messages) ? messages.join(' ') : String(messages);
      });
      setErrors(mapped);
      return;
    }

    notify.error(t('accounting.failedToSaveMovement'));
  };

  const handleSave = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      await monetaryMovementService.createMovement(form);
      notify.success(t('accounting.movementSaved'));
      onSaved();
    } catch (err) {
      console.error('Error saving monetary movement:', err);
      applyServerErrors(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('accounting.addMovement')}>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label" htmlFor="movementAccount">
            {t('accounting.accountName')}
          </label>
          <select
            id="movementAccount"
            value={form.account}
            onChange={handleChange('account')}
            className="form-select"
          >
            <option value="">{t('filters.none')}</option>
            {accounts.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
          {errors.account && <div className="form-error">{errors.account}</div>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="movementType">
            {t('accounting.movementType')}
          </label>
          <select
            id="movementType"
            value={form.movement_type}
            onChange={handleChange('movement_type')}
            className="form-select"
          >
            <option value="inflow">{t('accounting.movementTypes.inflow')}</option>
            <option value="outflow">{t('accounting.movementTypes.outflow')}</option>
            <option value="adjustment">{t('accounting.movementTypes.adjustment')}</option>
          </select>
        </div>
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label" htmlFor="movementEffectiveDate">
            {t('accounting.effectiveDate')}
          </label>
          <input
            id="movementEffectiveDate"
            type="date"
            value={form.effective_date}
            onChange={handleChange('effective_date')}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="movementAmount">
            {t('accounting.amount')}
          </label>
          <input
            id="movementAmount"
            type="text"
            inputMode="decimal"
            value={form.amount}
            onChange={handleChange('amount')}
            className="form-input"
          />
          {errors.amount && <div className="form-error">{errors.amount}</div>}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="movementDescription">
          {t('accounting.description')}
        </label>
        <input
          id="movementDescription"
          type="text"
          value={form.description}
          onChange={handleChange('description')}
          className="form-input"
        />
        {errors.description && <div className="form-error">{errors.description}</div>}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
};

export default RegisterMovementModal;
